import { Component } from '@theme/component';
import { addItemToCart } from '@theme/cart-add-utility';
import { createTracker, parseAnalyticsContext } from '@theme/analytics';
import { CartUpdateEvent } from '@theme/events';
import { fetchCart } from '@theme/utilities';

/** GTM triggers match these names. */
const EVENTS = /** @type {const} */ ({
  shown: 'cart_upsell_shown',
  added: 'cart_upsell_added',
  thresholdReached: 'cart_upsell_threshold_reached',
  failed: 'cart_upsell_failed',
});

/** A successful add normally ends with a reload; this releases the block if none comes. */
const ADD_LOCK_TIMEOUT = 8000;

/**
 * A custom element that manages cart recommendations.
 * @extends {Component}
 */
class CartRecommendations extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #reloadScheduled = false;

  /** The drawer is closed on page load: recommendations load once it is on screen. */
  #inView = false;

  /** @type {boolean} */
  #stale = true;

  /** @type {IntersectionObserver | null} */
  #viewObserver = null;

  /** @type {IntersectionObserver | null} */
  #shownObserver = null;

  /** @type {{ slideCount: string, hasRecommendations: string } | null} */
  #state = null;

  /** @type {string} */
  #errorText = '';

  /** @type {number} */
  #errorUntil = 0;

  /** @type {object | null} */
  #addLock = null;

  #tracker = createTracker(() => ({}));

  /**
   * Observed attributes that signal a cart update has been morphed onto this element.
   * When any of these attributes change on the live DOM element (via morph), we re-render.
   * data-upsell-gap: the free-shipping upsell depends on the amount still needed, which also
   * changes when only a quantity changes (same number of lines).
   * data-recommendations-performed: morph copies the header render's state, which is never
   * performed, so the loaded state is restored instead of showing the skeleton.
   */
  static observedAttributes = [
    'data-product-id',
    'data-cart-items-count',
    'data-upsell-gap',
    'data-recommendations-performed',
  ];

  connectedCallback() {
    super.connectedCallback();

    // The block itself is display:none while empty, so watch the drawer list around it
    const area = this.closest('.cart-drawer__items') ?? this.parentElement ?? this;
    this.#viewObserver = new IntersectionObserver((entries) => {
      this.#inView = entries.some((entry) => entry.isIntersecting);
      if (this.#inView && this.#stale) this.#loadRecommendations();
    });
    this.#viewObserver.observe(area);
  }

  /**
   * Fires when morph updates an observed attribute on the live element.
   *
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === null || oldValue === newValue) return;

    if (name === 'data-recommendations-performed') {
      // Restore after morph has copied the rest of the attributes
      queueMicrotask(() => this.#applyState());
      return;
    }

    this.#stale = true;
    this.#scheduleRecommendationsReload();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    this.#viewObserver?.disconnect();
    this.#shownObserver?.disconnect();
  }

  /**
   * Batches multiple synchronous attribute updates into a single reload.
   */
  #scheduleRecommendationsReload() {
    if (this.#reloadScheduled) return;
    this.#reloadScheduled = true;

    queueMicrotask(() => {
      this.#reloadScheduled = false;
      if (!this.isConnected || !this.#inView) return;
      this.#loadRecommendations();
    });
  }

  /**
   * Loads the recommendations.
   */
  async #loadRecommendations() {
    const { productId, sectionId, intent, url } = this.dataset;
    const id = this.id;

    if (!productId || !sectionId || !url || !id) return;

    const requestUrl = `${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent || 'related'}`;

    this.#stale = false;
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const response = await fetch(requestUrl, {
        signal: controller.signal,
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const htmlString = await response.text();
      if (controller.signal.aborted) return;

      const html = document.createElement('div');
      html.innerHTML = htmlString || '';

      /** @type {HTMLElement | null} */
      const recommendations = html.querySelector(`cart-recommendations-component[id="${id}"]`);
      if (!recommendations?.innerHTML?.trim()) {
        this.#hide();
        return;
      }

      this.#render(recommendations);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Cart recommendations error:', error);
        this.#hide();
      }
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * @param {HTMLElement} recommendations - The recommendations to render.
   */
  #render(recommendations) {
    this.#state = {
      slideCount: recommendations.dataset.slideCount || '0',
      hasRecommendations: recommendations.dataset.hasRecommendations || 'false',
    };
    this.#applyState();
    this.innerHTML = recommendations.innerHTML;
    this.#addLock = null;

    // Keep a fresh add error visible through the reload that follows the cart resync
    if (this.#errorText && Date.now() < this.#errorUntil) this.#showError();
    this.#errorText = '';

    this.#observeShown();
  }

  /**
   * Hides the component.
   */
  #hide() {
    this.#state = { slideCount: '0', hasRecommendations: 'false' };
    this.#applyState();
    this.#addLock = null;
    this.#shownObserver?.disconnect();
  }

  #applyState() {
    if (!this.#state) return;

    this.dataset.recommendationsPerformed = 'true';
    this.dataset.slideCount = this.#state.slideCount;
    this.dataset.hasRecommendations = this.#state.hasRecommendations;
  }

  /**
   * Reports the upsell once its heading is actually on screen.
   */
  #observeShown() {
    this.#shownObserver?.disconnect();
    const heading = this.querySelector('.cart-recommendations__heading[data-analytics]');
    if (!(heading instanceof HTMLElement)) return;

    const shown = parseAnalyticsContext(heading.dataset.analytics);
    const key = `${this.dataset.productId}:${shown.upsell_gap}`;
    this.#shownObserver = new IntersectionObserver((entries, observer) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      this.#tracker.trackOnce(key, EVENTS.shown, shown);
    });
    this.#shownObserver.observe(heading);
  }

  #showError() {
    let error = this.querySelector('.cart-recommendations__error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'cart-recommendations__error';
      error.setAttribute('role', 'alert');
      this.prepend(error);
    }
    error.textContent = this.#errorText;
  }

  async #syncCartAfterError() {
    try {
      const cart = await fetchCart();
      this.dispatchEvent(
        new CartUpdateEvent(cart, this.id, {
          source: 'cart-recommendations',
          itemCount: cart.item_count,
        })
      );
    } catch {
      // The cart may have changed, but we cannot read it.
    }
  }

  /**
   * Adds a recommended product to the cart: one item, one full case for case-pack products,
   * or data-quantity units (free-shipping upsell cards).
   * @param {string} variantId - The variant ID to add.
   */
  async addToCart(variantId) {
    const button = this.querySelector(`[data-variant-id="${variantId}"]`);
    // One add at a time until the reload replaces the cards, so a card cannot be added twice
    if (!(button instanceof HTMLElement) || this.#addLock) return;
    const lock = {};
    this.#addLock = lock;
    button.setAttribute('aria-busy', 'true');
    this.#errorText = '';
    this.querySelector('.cart-recommendations__error')?.remove();

    const caseSize = parseInt(button.getAttribute('data-case-size') || '1', 10) || 1;
    const quantity = parseInt(button.getAttribute('data-quantity') || '1', 10) || 1;
    const caseName = button.getAttribute('data-case-name');
    const caseLabel = button.getAttribute('data-case-label');
    const properties = caseName && caseLabel ? { [caseName]: caseLabel } : null;
    const upsell = parseAnalyticsContext(button.dataset.analytics);

    try {
      await addItemToCart(this, variantId, 'cart-recommendations', caseSize > 1 ? caseSize : quantity, properties);
    } catch (error) {
      this.#addLock = null;
      button.removeAttribute('aria-busy');
      console.error('Error adding recommendation to cart:', error);

      // addItemToCart throws a plain Error with Shopify's description; anything else is technical
      const isCartError = error instanceof Error && error.name === 'Error';
      if (upsell.upsell_type) {
        this.#tracker.track(EVENTS.failed, { ...upsell, error_reason: isCartError ? 'cart_error' : 'network_error' });
      }
      this.#errorText = (isCartError && error.message) || this.dataset.errorText || '';
      this.#errorUntil = Date.now() + 10000;
      this.#showError();
      await this.#syncCartAfterError();
      return;
    }

    setTimeout(() => {
      if (this.#addLock !== lock) return;
      this.#addLock = null;
      button.removeAttribute('aria-busy');
    }, ADD_LOCK_TIMEOUT);

    if (upsell.upsell_type) this.#trackAdded(upsell);
  }

  /**
   * @param {Record<string, unknown>} upsell - Card context from data-analytics.
   */
  async #trackAdded(upsell) {
    let cartValueAfter;
    try {
      // Not the shared fetchCart promise: it may have started before this add
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' })).json();
      cartValueAfter = cart.total_price / 100;
    } catch {
      // Reported without the cart value
    }

    const after = { cart_value_after: cartValueAfter };
    this.#tracker.track(EVENTS.added, { ...upsell, ...after });
    if (cartValueAfter !== undefined && cartValueAfter >= Number(upsell.threshold)) {
      this.#tracker.track(EVENTS.thresholdReached, { ...upsell, ...after });
    }
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendations);
}
